import { z } from 'zod';
import { isPermissionCode } from './permissions.ts';
import { isValidCnpj, normalizeTaxId } from './tax-id.ts';

/** Idiomas suportados pela interface. */
export const LOCALES = ['pt-BR', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const currencyCode = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Código ISO 4217 com 3 letras maiúsculas');
export const countryCode = z
  .string()
  .regex(/^[A-Z]{2}$/, 'Código ISO 3166 com 2 letras maiúsculas');
export const uuid = z.uuid();
export const timezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Fuso horário inválido (ex.: America/Sao_Paulo)');
export const localizedText = z.object({ 'pt-BR': trimmed(200), en: trimmed(200) });

/** Código curto em maiúsculas, usado em relatórios e integrações (ex.: KRAKEN, HUB-US). */
export const shortCode = z
  .string()
  .trim()
  .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, - ou _');

/**
 * Empresa ou filial do Brasil: o documento, quando informado, é um CNPJ válido (numérico ou
 * alfanumérico). Fora do Brasil, o documento local fica como foi digitado.
 */
function checkBrazilianCnpj(v: { country: string; taxId?: string | null }, ctx: z.RefinementCtx) {
  if (v.country === 'BR' && v.taxId && !isValidCnpj(normalizeTaxId(v.taxId))) {
    ctx.addIssue({ code: 'custom', message: 'CNPJ inválido', path: ['taxId'] });
  }
}

/** CNPJ guardado sem pontuação, como o dos parceiros: é assim que ele é comparado. */
function normalizeBrazilianCnpj<T extends { country: string; taxId?: string | null }>(v: T): T {
  return v.country === 'BR' && v.taxId ? { ...v, taxId: normalizeTaxId(v.taxId) } : v;
}

export const LEGAL_ENTITY_KINDS = ['holding', 'operating', 'other'] as const;
export const BRANCH_KINDS = [
  'headquarters',
  'branch',
  'warehouse',
  'hub',
  'factory',
  'office',
  'store',
  'other',
] as const;
export const SCOPE_TYPES = ['group', 'legal_entity', 'branch'] as const;
export const EXCHANGE_RATE_SOURCES = ['BCB_PTAX', 'ECB', 'STRIPE', 'MANUAL'] as const;
/**
 * Tipos de taxa (ADR 0032). Efetivas (do dia): compra, venda e média. Do planejamento: média do
 * mês, fechamento do mês e orçamentária (fixada para o exercício).
 */
export const EXCHANGE_RATE_TYPES = ['mid', 'buy', 'sell', 'average', 'closing', 'budget'] as const;
/** As efetivas: as únicas usadas para converter valores de pedidos e aprovações. */
export const EFFECTIVE_RATE_TYPES = ['mid', 'buy', 'sell'] as const;

export const legalEntityInput = z
  .object({
    code: shortCode,
    legalName: trimmed(200),
    tradeName: optionalText(200),
    kind: z.enum(LEGAL_ENTITY_KINDS),
    country: countryCode,
    taxId: optionalText(40),
    functionalCurrency: currencyCode,
    taxRegime: optionalText(60),
    timezone,
    defaultLocale: z.enum(LOCALES),
    parentLegalEntityId: uuid.nullable().optional(),
    ownershipPct: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Percentual com até 4 casas decimais')
      .refine((v) => Number(v) > 0 && Number(v) <= 100, 'Entre 0 e 100')
      .nullable()
      .optional(),
    isActive: z.boolean().default(true),
  })
  .refine((v) => (v.ownershipPct == null) === (v.parentLegalEntityId == null), {
    message: 'Informe a empresa controladora e o percentual juntos',
    path: ['ownershipPct'],
  })
  .superRefine(checkBrazilianCnpj)
  .transform(normalizeBrazilianCnpj);
export type LegalEntityInput = z.infer<typeof legalEntityInput>;

export const branchInput = z
  .object({
    legalEntityId: uuid,
    code: shortCode,
    name: trimmed(200),
    kind: z.enum(BRANCH_KINDS),
    country: countryCode,
    timezone,
    taxId: optionalText(40),
    stateRegistration: optionalText(40),
    municipalRegistration: optionalText(40),
    address: z
      .object({
        line1: optionalText(200),
        line2: optionalText(200),
        city: optionalText(120),
        region: optionalText(120),
        postalCode: optionalText(20),
      })
      .default({}),
    isActive: z.boolean().default(true),
  })
  .superRefine(checkBrazilianCnpj)
  .transform(normalizeBrazilianCnpj);
export type BranchInput = z.infer<typeof branchInput>;

/** Área de uma empresa (Marketing, Produção...), com quem aprova as compras dela. */
export const departmentInput = z
  .object({
    /** Vazio = departamento corporativo, comum a todas as empresas do grupo. */
    legalEntityId: uuid.nullable().optional(),
    code: shortCode,
    name: trimmed(200),
    /** Centro de custo oficial (mesma empresa; o corporativo, de qualquer uma). Vazio = nenhum. */
    costCenterId: uuid.nullable().optional(),
    managerUserId: uuid.nullable().optional(),
    substituteUserId: uuid.nullable().optional(),
    isActive: z.boolean().default(true),
    /** Código do departamento no NetSuite (só a referência, sem integração). */
    netsuiteCode: optionalText(100),
  })
  .refine((v) => !v.substituteUserId || v.substituteUserId !== v.managerUserId, {
    message: 'O substituto precisa ser outra pessoa',
    path: ['substituteUserId'],
  });
export type DepartmentInput = z.infer<typeof departmentInput>;

/** Cargos do departamento (ADR 0096): famílias, subfamílias e exceções justificadas por cargo. */
export const departmentJobScopeInput = z.object({
  familyIds: z.array(uuid).max(100),
  subfamilyIds: z.array(uuid).max(200),
  exceptions: z
    .array(
      z.object({
        jobId: uuid,
        justification: z.string().trim().min(3, 'Informe a justificativa da exceção').max(500),
      }),
    )
    .max(200),
});
export type DepartmentJobScopeInput = z.infer<typeof departmentJobScopeInput>;

export const userInput = z.object({
  email: z.email().trim().toLowerCase().max(254),
  displayName: trimmed(200),
  locale: z.enum(LOCALES).default('pt-BR'),
  timezone: timezone.default('America/Sao_Paulo'),
  isActive: z.boolean().default(true),
  /** Tipo da conta (ADR 0042): só a conta de pessoa aprova pedidos de acesso. */
  accountKind: z.enum(['person', 'generic', 'technical']).optional(),
});
export type UserInput = z.infer<typeof userInput>;

export const roleInput = z.object({
  code: shortCode,
  name: localizedText,
  description: z.object({ 'pt-BR': z.string().max(500), en: z.string().max(500) }).optional(),
  permissions: z
    .array(z.string().refine(isPermissionCode, 'Permissão desconhecida'))
    .max(500)
    .transform((list) => [...new Set(list)].sort()),
  isActive: z.boolean().default(true),
});
export type RoleInput = z.infer<typeof roleInput>;

const justification = z
  .string()
  .trim()
  .min(10, 'Explique o motivo em pelo menos 10 caracteres')
  .max(1000);

const roleAssignmentFields = {
  userId: uuid,
  roleId: uuid,
  scopeType: z.enum(SCOPE_TYPES),
  legalEntityId: uuid.nullable().optional(),
  branchId: uuid.nullable().optional(),
  /** Quem concede confirma que as permissões "só do grupo" do perfil não vão valer aqui. */
  acknowledgeGroupOnly: z.boolean().optional(),
};
const consistentScope = (v: {
  scopeType: (typeof SCOPE_TYPES)[number];
  legalEntityId?: string | null;
  branchId?: string | null;
}) =>
  (v.scopeType === 'group' && !v.legalEntityId && !v.branchId) ||
  (v.scopeType === 'legal_entity' && !!v.legalEntityId && !v.branchId) ||
  (v.scopeType === 'branch' && !!v.branchId);
const scopeMessage = {
  message: 'Escopo inconsistente com empresa/filial informadas',
  path: ['scopeType'],
};

/** Concessão direta. Perfil que não é normal exige o motivo (ADR 0075). */
export const roleAssignmentInput = z
  .object({ ...roleAssignmentFields, justification: justification.optional() })
  .refine(consistentScope, scopeMessage);
export type RoleAssignmentInput = z.infer<typeof roleAssignmentInput>;

/**
 * Classificação de um perfil na matriz de perfis delegáveis (ADR 0042): normal (concessão direta),
 * sensível (aprovação de um Administrador), restrito e crítico (só o Administrador pede, outro
 * aprova; retirar perfil crítico também exige aprovação).
 */
export const ACCESS_TIERS = ['normal', 'sensitive', 'restricted', 'critical'] as const;
export type AccessTier = (typeof ACCESS_TIERS)[number];

/** Pedido de concessão de perfil que exige aprovação. */
export const accessGrantRequestInput = z
  .object({ ...roleAssignmentFields, justification })
  .refine(consistentScope, scopeMessage);
export type AccessGrantRequestInput = z.infer<typeof accessGrantRequestInput>;

/** Pedido de retirada de perfil crítico. */
export const accessRevokeRequestInput = z.object({ assignmentId: uuid, justification });

/** Bloqueio emergencial de uma conta (incidente de segurança). */
export const emergencyBlockInput = z.object({ justification });

/** Pedido de desbloqueio de uma conta bloqueada em emergência. */
export const accessUnblockRequestInput = z.object({ userId: uuid, justification });

/** Pedido excepcional para que uma conta genérica ou técnica passe a ser de pessoa. */
export const accessReclassifyRequestInput = z.object({ userId: uuid, justification });

/**
 * Liberação de responsável único (ADR 0043): o próprio titular pede, com justificativa de pelo
 * menos 30 caracteres. O banco confere de novo.
 */
export const SINGLE_HOLDER_JUSTIFICATION_MIN = 30;
export const singleHolderRequestInput = z.object({
  justification: z
    .string()
    .trim()
    .min(SINGLE_HOLDER_JUSTIFICATION_MIN, 'Explique o motivo com pelo menos 30 caracteres')
    .max(1000),
});

/** Motivos pelos quais a conta não pode usar a liberação (calculados pelo banco). */
export const SINGLE_HOLDER_REASONS = [
  'role_not_allowed',
  'account_blocked',
  'generic_account',
  'not_admin',
  'no_active_person',
  'regular_governance',
  'already_has_role',
] as const;
export type SingleHolderReason = (typeof SINGLE_HOLDER_REASONS)[number];

/** Decisão de um pedido de acesso (a recusa exige o motivo). */
export const accessDecisionInput = z.object({ note: trimmed(1000).optional() });

/** Valor decimal como texto, para nunca passar dinheiro ou câmbio por ponto flutuante. */
export const decimalString = (intDigits: number, scale: number) =>
  z
    .string()
    .trim()
    .regex(
      new RegExp(`^\\d{1,${intDigits}}(\\.\\d{1,${scale}})?$`),
      `Número com até ${scale} casas decimais, usando ponto`,
    );

export const exchangeRateInput = z
  .object({
    rateDate: z.iso.date(),
    baseCurrency: currencyCode,
    quoteCurrency: currencyCode,
    rate: decimalString(10, 10).refine((v) => Number(v) > 0, 'A taxa precisa ser maior que zero'),
    rateType: z.enum(EXCHANGE_RATE_TYPES).default('mid'),
  })
  .refine((v) => v.baseCurrency !== v.quoteCurrency, {
    message: 'As moedas precisam ser diferentes',
    path: ['quoteCurrency'],
  });
export type ExchangeRateInput = z.infer<typeof exchangeRateInput>;

/** Taxa de correção: substitui uma taxa gravada (que continua no histórico), com justificativa. */
export const exchangeRateCorrectionInput = z.object({
  rate: decimalString(10, 10).refine((v) => Number(v) > 0, 'A taxa precisa ser maior que zero'),
  reason: z.string().trim().min(3, 'Explique o motivo da correção').max(1000),
});

/** Artigo da Ajuda: textos em Markdown, nos dois idiomas. */
export const helpArticleInput = z.object({
  slug: z
    .string()
    .trim()
    .max(80)
    .regex(
      /^[a-z0-9]+(-[a-z0-9]+)*$/,
      'Use letras minúsculas, números e hífen (ex.: como-pedir-acesso)',
    )
    .refine((s) => s !== 'new', 'Este endereço é reservado'),
  title: localizedText,
  summary: z.object({ 'pt-BR': trimmed(400), en: trimmed(400) }),
  body: z.object({ 'pt-BR': trimmed(100_000), en: trimmed(100_000) }),
  isPublished: z.boolean(),
  sortOrder: z.number().int().min(0).max(9999),
});

export const pageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
